// Closed vocabularies shared by the web form, the API and the connectors.
// Adding a value here is a contract change: update the GHL field map and the GlassBiller packet too.

export const GLASS_POSITIONS = [
  "windshield",
  "back_glass",
  "door_front_left",
  "door_front_right",
  "door_rear_left",
  "door_rear_right",
  "quarter_left",
  "quarter_right",
  "vent_left",
  "vent_right",
  "sunroof",
] as const;
export type GlassPosition = (typeof GLASS_POSITIONS)[number];

export const DAMAGE_TYPES = ["chip", "crack", "shattered", "other"] as const;
export type DamageType = (typeof DAMAGE_TYPES)[number];

export const JOB_TYPES = ["insurance", "dealer_pay", "customer_pay", "warranty"] as const;
export type JobType = (typeof JOB_TYPES)[number];

export const VEHICLE_LOCATIONS = ["service_lane", "used_car_lot", "body_shop", "customer_address"] as const;
export type VehicleLocation = (typeof VEHICLE_LOCATIONS)[number];

export const PRIORITIES = ["normal", "today", "asap"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PHOTO_KINDS = ["damage", "full_glass", "vin_plate", "odometer", "insurance_card", "other"] as const;
export type PhotoKind = (typeof PHOTO_KINDS)[number];

/** Lifecycle. Happy path left to right; cancelled and duplicate are off-ramps. */
export const JOB_STATUSES = [
  "submitted",
  "received",
  "scheduled",
  "installed",
  "invoiced",
  "paid",
  "cancelled",
  "duplicate",
] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/** AGW's four shops. A store is served from one of them. */
export const SHOPS = ["fort_myers", "polk", "charleston", "greer"] as const;
export type Shop = (typeof SHOPS)[number];

/**
 * Four logins (set 2026-10-21 with Briana's team):
 * - sales_rep: AGW's own reps placed at 2-3 dealerships. Sends claims, sees only their own.
 * - sales_manager: sees the reps assigned to them (User.managerId) and those reps' claims.
 * - dealership: one read-only login per dealership. Counts and the job list, no phone or policy.
 * - agw_admin: everything, plus the office fields, people and dealerships. Never sees or edits another admin.
 * - super_admin: an admin who also adds, sees and edits admins (set 2026-10-24).
 */
export const ROLES = ["sales_rep", "sales_manager", "dealership", "agw_admin", "super_admin"] as const;
export type Role = (typeof ROLES)[number];

/** States AGW serves. Each dealership sits in one. */
export const STATES = ["FL", "SC", "NC"] as const;
export type UsState = (typeof STATES)[number];

/**
 * The office's claim status: the "Scheduled" column of the Claims sheet, cleaned up.
 * In GHL each one is a stage of the Dealer Claims pipeline. "installed" is what every report counts.
 */
export const CLAIM_STATUSES = [
  "new",
  "pending_schedule",
  "scheduled",
  "installed",
  "needs_rescheduled",
  "call_later",
  "no_contact",
  "three_day_no_contact",
  "inspection",
  "pending_coverage",
  "pending_approval",
  "no_coverage",
  "oem_requested",
  "repair",
  "repair_loop",
  "cash_quote_pending",
  "cash_quote_scheduled",
  "no_damage",
  "cancelled",
  "duplicate",
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number];

/** GlassBiller's own job state: the "GB Status" column. */
export const GB_STATUSES = [
  "needs_built",
  "built",
  "not_built",
  "pending_contact",
  "pending_dispatch",
  "cash_quote",
  "cancelled",
  "void",
  "duplicate",
] as const;
export type GbStatus = (typeof GB_STATUSES)[number];

/** Carrier pick list, most used first (from 2,331 sheet rows). "Other" opens a text box. */
export const INSURANCE_CARRIERS = [
  "Progressive", "State Farm", "Geico", "Allstate", "USAA", "Travelers/Standard Fire", "Auto Owners/Southern Owners",
  "Direct Auto", "Liberty Mutual", "AAA", "National General", "Bristol West", "Infinity", "Nationwide", "Dairyland",
  "Amica", "Safeco", "Farm Bureau", "Ocean Harbor", "Mercury", "Hartford", "Farmers", "Root", "Esurance",
  "American Family", "The General", "Cash (Quote)", "Other",
] as const;

export const SYNC_TARGETS = ["glassbiller", "ghl"] as const;
export type SyncTarget = (typeof SYNC_TARGETS)[number];

export const SYNC_STATES = ["pending", "awaiting_manual", "synced", "failed"] as const;
export type SyncState = (typeof SYNC_STATES)[number];

export const MAX_PHOTOS_PER_JOB = 15;
/** Largest photo the phone may upload and the server will keep. */
export const MAX_PHOTO_BYTES = 15 * 1024 * 1024;
/** Largest preview (thumbnail) JPEG. The phone makes them about 25 KB. */
export const MAX_THUMB_BYTES = 200 * 1024;
